import { inject, Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { Observable, of } from 'rxjs';

import { CsvAsStringArrays, KeywordSearchImpressionsTypeExtended } from '@malou-io/package-utils';

import { TopKeywordSearchImpressions } from '#modules/statistics-seo/models/aggregated-seo/top-keyword-search-impressions.interface';
import {
    AbstractCsvService,
    DataWithNilExcluded,
} from '#modules/statistics/components/download-insights-modal/models/csv-service.abstract';
import { AggregatedStatisticsStore } from '#modules/statistics/stores/aggregated-store/aggregated-statistics.store';

interface Data {
    keywordSearchImpressions: TopKeywordSearchImpressions;
    dates: { startDate: string; endDate: string };
}

@Injectable()
export class AggregatedKeywordSearchImpressionsService extends AbstractCsvService<Data> {
    private readonly _aggregatedStatisticsStore = inject(AggregatedStatisticsStore);

    protected _getData$(): Observable<Data> {
        const topKeywordSearchImpressions = this._aggregatedStatisticsStore.topKeywordSearchImpressionsData();
        const filters = this._aggregatedStatisticsStore.filters();
        const monthYearPeriod = filters.monthYearPeriod;
        const startDate = DateTime.fromObject(monthYearPeriod.startMonthYear).startOf('month').toISODate();
        const endDate = DateTime.fromObject(monthYearPeriod.endMonthYear).endOf('month').toISODate();
        return of({
            keywordSearchImpressions: topKeywordSearchImpressions,
            dates: { startDate, endDate },
        });
    }

    protected override _isDataValid(data: Data): boolean {
        return !!data;
    }

    protected override _getCsvHeaderRow(): string[] {
        return ['Start Date', 'End Date', 'Keyword', 'Type', 'Average Appearances', 'Evolution of Appearances', 'Establishments'];
    }

    protected override _getCsvDataRows(data: DataWithNilExcluded<Data>): CsvAsStringArrays {
        const { keywordSearchImpressions, dates } = data;
        const { startDate, endDate } = dates;
        const csvDataTypes = [KeywordSearchImpressionsTypeExtended.MALOU_SELECTED, KeywordSearchImpressionsTypeExtended.DISCOVERY];
        return csvDataTypes
            .map((type) =>
                keywordSearchImpressions[type].map((impression) => {
                    const keyword = impression.keywordSearch;
                    const averageAppearances = impression.avgValue.toString();
                    const evolutionOfAppearances = (impression.evolution ?? '-').toString();
                    const locations = impression.restaurants.map((restaurant) => restaurant.name).join(', ');
                    return [startDate, endDate, keyword, type, averageAppearances, evolutionOfAppearances, locations];
                })
            )
            .flat();
    }
}
